import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Controller, type FieldErrors, useFieldArray, useForm, useWatch } from 'react-hook-form';
import dayjs from '~/timeInit';

import { Add as AddIcon, Delete as DeleteIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';

import {
  useCreateClaimCampaign,
  useListClaimRecords,
  useTestMatchCheck,
  useUpdateClaimCampaign,
} from '~/hooks/apis/claimCampaigns';
import useCurrentUser from '~/hooks/useCurrentUser';

import {
  CLAIM_ACTION_TYPES,
  CLAIM_CAMPAIGN_STATUSES,
  CLAIM_CAMPAIGN_TYPES,
  CLAIM_TARGET_TIERS,
  CLAIM_VALIDATOR_TYPES,
  type ClaimActionType,
  type ClaimCampaign,
  type ClaimCampaignStatus,
  type ClaimCampaignType,
  type ClaimMatchCheckType,
  type ClaimTargetTier,
  type ClaimValidatorType,
  type CreateClaimCampaignBody,
  EVIDENCE_FIELD_TYPES,
  EXTERNAL_API_METHODS,
  type EvidenceFieldType,
  type ExternalApiMethod,
  MAX_APPEALS,
  MIN_APPEALS,
  type UpdateClaimCampaignBody,
} from '~/types/claimCampaignTypes';

import { titleCase } from '~/utils/stringUtils';

import {
  formatEmailVerificationDomains,
  getEmailVerificationDomainsHelperText,
  getEmailVerificationDomainsPayload,
  validateEmailVerificationDomainsInput,
} from './Claims/emailVerification';

interface CreateClaimCampaignDialogProps {
  open: boolean;
  onClose: () => void;
  /** When provided the dialog operates in edit mode. */
  campaign?: ClaimCampaign | null;
}

/** Form shape for a single reward mapping row (DYNAMIC_TIER_UPGRADE) */
interface RewardMappingFormRow {
  id: string;
  match: Record<string, string>;
  targetTier: ClaimTargetTier;
  remarks: string;
}

/** Form shape for a single action entry */
interface ActionFormEntry {
  type: ClaimActionType;
  /** TIER_UPGRADE fields */
  targetTier: ClaimTargetTier;
  remarks: string;
  /** BONUS_POINTS fields */
  bonusPoints: string;
  /** STATUS_CREDITS fields */
  statusCredits: string;
  bonusStatusCredits: string;
  /** Shared field for BONUS_POINTS and STATUS_CREDITS */
  activityDescription: string;
  /** DYNAMIC_TIER_UPGRADE fields */
  rewardMapping: RewardMappingFormRow[];
}

/** Full form data shape */
interface FormData {
  name: string;
  description: string;
  emailVerificationDomains: string;
  status: ClaimCampaignStatus;
  campaignType: ClaimCampaignType;
  validatorType: ClaimValidatorType;
  /** EXTERNAL_API validator fields */
  externalEndpoint: string;
  externalMethod: ExternalApiMethod;
  externalSuccessPath: string;
  externalErrorPath: string;
  externalTimeout: string;
  /** Approval fields (for any validator type) */
  requireApproval: boolean;
  evidenceFields: Array<{ name: string; label: string; type: EvidenceFieldType; options: string }>;
  /** MATCH campaign fields */
  bqQuery: string;
  checks: Array<{ key: string; eligibilityTemplate: string }>;
  /** Actions */
  actions: ActionFormEntry[];
  maxClaimsPerUser: string;
  maxAppeals: string;
  enrollmentValidityDays: string;
  startDate: string;
  endDate: string;
}

const DEFAULT_ACTION: ActionFormEntry = {
  type: 'BONUS_POINTS',
  targetTier: 'silver',
  remarks: '',
  bonusPoints: '',
  statusCredits: '',
  bonusStatusCredits: '',
  activityDescription: '',
  rewardMapping: [],
};

const DEFAULT_VALUES: FormData = {
  name: '',
  description: '',
  emailVerificationDomains: '',
  status: 'DRAFT',
  campaignType: 'NORMAL',
  validatorType: 'PREGEN_CODE',
  externalEndpoint: '',
  externalMethod: 'POST',
  externalSuccessPath: '',
  externalErrorPath: '',
  externalTimeout: '5000',
  requireApproval: false,
  evidenceFields: [],
  bqQuery: '',
  checks: [{ key: '', eligibilityTemplate: '' }],
  actions: [{ ...DEFAULT_ACTION }],
  maxClaimsPerUser: '1',
  maxAppeals: '1',
  enrollmentValidityDays: '30',
  startDate: '',
  endDate: '',
};

/** Human-readable labels for validator types */
const VALIDATOR_LABELS: Record<ClaimValidatorType, string> = {
  PREGEN_CODE: 'Pre-generated Code',
  SHARED_CODE: 'Shared Code',
  EXTERNAL_API: 'External API',
  DIRECT: 'Direct',
};

/** Human-readable labels for action types */
const ACTION_TYPE_LABELS: Record<ClaimActionType, string> = {
  TIER_UPGRADE: 'Tier Upgrade',
  BONUS_POINTS: 'Bonus Points',
  STATUS_CREDITS: 'Status Credits',
  DYNAMIC_TIER_UPGRADE: 'Dynamic Tier Upgrade',
};

/** Extracts human-readable field names from react-hook-form errors. */
function formatValidationErrors(errors: FieldErrors<FormData>): string {
  const fields: string[] = [];
  for (const [key, value] of Object.entries(errors)) {
    if (Array.isArray(value)) {
      value.forEach((item, i) => {
        if (item) Object.keys(item).forEach((k) => fields.push(`${key}[${i}].${k}`));
      });
    } else if (value?.message) {
      fields.push(key);
    }
  }
  return fields.length > 0 ? `Please fix: ${fields.join(', ')}` : 'Please fill in all required fields';
}

/** Filters and maps form checks to the API shape. */
function getValidChecks(checks: FormData['checks']) {
  return checks
    .filter((c) => c.key.trim())
    .map((c) => ({
      key: c.key,
      eligibilityTemplate: c.eligibilityTemplate,
    }));
}

/**
 * Converts a campaign's `actions` (Record<string, unknown>[]) back into ActionFormEntry[].
 */
function parseTier(raw: unknown): ClaimTargetTier {
  return raw === 'gold' ? 'gold' : raw === 'platinum' ? 'platinum' : 'silver';
}

function mapActionsToForm(actions: Array<Record<string, unknown>>): Array<ActionFormEntry> {
  return actions.map((a) => {
    const rawType = a.type;
    const type: ClaimActionType =
      rawType === 'TIER_UPGRADE'
        ? 'TIER_UPGRADE'
        : rawType === 'STATUS_CREDITS'
          ? 'STATUS_CREDITS'
          : rawType === 'DYNAMIC_TIER_UPGRADE'
            ? 'DYNAMIC_TIER_UPGRADE'
            : 'BONUS_POINTS';

    const rewardMapping: Array<RewardMappingFormRow> = Array.isArray(a.rewardMapping)
      ? (a.rewardMapping as Array<Record<string, unknown>>).map((r, i) => ({
          id: String(i),
          match: (r.match ?? {}) as Record<string, string>,
          targetTier: parseTier(r.targetTier),
          remarks: String(r.remarks ?? ''),
        }))
      : [];

    return {
      type,
      targetTier: parseTier(a.targetTier),
      remarks: String(a.remarks ?? ''),
      bonusPoints: a.bonusPoints != null ? String(a.bonusPoints) : '',
      statusCredits: a.statusCredits != null ? String(a.statusCredits) : '',
      bonusStatusCredits: a.bonusStatusCredits != null ? String(a.bonusStatusCredits) : '',
      activityDescription: String(a.activityDescription ?? ''),
      rewardMapping,
    };
  });
}

/**
 * Converts a ClaimCampaign entity into form default values for editing.
 */
function campaignToFormValues(campaign: ClaimCampaign): FormData {
  const vc = campaign.validatorConfig ?? {};
  const mc = campaign.matchCheckConfig;
  const method = vc.method;
  const evidenceFields =
    campaign.evidenceFields?.map((f) => ({
      name: f.name,
      label: f.label,
      type: f.type ?? 'text',
      options: (f.options ?? []).join(', '),
    })) ?? [];
  return {
    name: campaign.name,
    description: campaign.description ?? '',
    emailVerificationDomains: formatEmailVerificationDomains(campaign.emailVerificationDomains),
    status: campaign.status,
    campaignType: campaign.type,
    validatorType: campaign.validatorType,
    externalEndpoint: String(vc.endpoint ?? ''),
    externalMethod: method === 'GET' ? 'GET' : 'POST',
    externalSuccessPath: String(vc.successPath ?? ''),
    externalErrorPath: String(vc.errorPath ?? ''),
    externalTimeout: vc.timeout != null ? String(vc.timeout) : '5000',
    requireApproval: campaign.requireApproval ?? false,
    evidenceFields,
    bqQuery: mc?.bqQuery ?? '',
    checks: mc?.checks?.map((c) => ({
      key: c.key ?? '',
      eligibilityTemplate: c.eligibilityTemplate ?? '',
    })) ?? [{ key: '', eligibilityTemplate: '' }],
    actions: campaign.actions.length > 0 ? mapActionsToForm(campaign.actions) : [{ ...DEFAULT_ACTION }],
    maxClaimsPerUser: String(campaign.maxClaimsPerUser),
    maxAppeals: String(campaign.maxAppeals ?? 1),
    enrollmentValidityDays: String(campaign.enrollmentValidityDays ?? 30),
    startDate: campaign.startDate ? dayjs(campaign.startDate).format('YYYY-MM-DDTHH:mm') : '',
    endDate: campaign.endDate ? dayjs(campaign.endDate).format('YYYY-MM-DDTHH:mm') : '',
  };
}

export default function CreateClaimCampaignDialog({ open, onClose, campaign }: CreateClaimCampaignDialogProps) {
  const isEditMode = !!campaign;
  const { enqueueSnackbar } = useSnackbar();
  const [isEmailVerificationDomainsFocused, setIsEmailVerificationDomainsFocused] = useState(false);

  const { data: claimsData } = useListClaimRecords(
    campaign?.id ?? '',
    { limit: 1 },
    {
      enabled: open && isEditMode,
    },
  );
  const fieldsLocked = isEditMode && (claimsData?.result?.pagination?.total ?? 0) > 0;

  const { user } = useCurrentUser();
  const createMutation = useCreateClaimCampaign();
  const updateMutation = useUpdateClaimCampaign();
  const testMatchMutation = useTestMatchCheck();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const { control, handleSubmit, reset, watch, getValues, trigger } = useForm<FormData>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onBlur',
  });

  const watchedChecks = useWatch({ control, name: 'checks' });
  const watchedEvidenceFields = useWatch({ control, name: 'evidenceFields' });

  /** Dropdown evidence fields with parsed options — used for DYNAMIC_TIER_UPGRADE reward mapping. */
  const dropdownFields = (watchedEvidenceFields ?? [])
    .filter((f) => f.type === 'dropdown' && f.name.trim())
    .map((f) => ({
      name: f.name,
      label: f.label || f.name,
      options: (f.options ?? '')
        .split(',')
        .map((o) => o.trim())
        .filter(Boolean),
    }));

  const { fields, append, remove } = useFieldArray({ control, name: 'actions' });
  const {
    fields: evidenceFieldEntries,
    append: appendEvidenceField,
    remove: removeEvidenceField,
  } = useFieldArray({
    control,
    name: 'evidenceFields',
  });

  const {
    fields: checkEntries,
    append: appendCheck,
    remove: removeCheck,
  } = useFieldArray({
    control,
    name: 'checks',
  });

  const campaignType = watch('campaignType');
  const validatorType = watch('validatorType');
  const bqQuery = watch('bqQuery');
  const requireApproval = watch('requireApproval');

  // Reset form when dialog opens — pre-fill with campaign data when editing
  useEffect(() => {
    if (open) {
      reset(campaign ? campaignToFormValues(campaign) : DEFAULT_VALUES);
      setIsEmailVerificationDomainsFocused(false);
    }
  }, [open, reset, campaign]);

  /**
   * Builds the API request body from the form data.
   */
  const buildPayload = useCallback((data: FormData): CreateClaimCampaignBody => {
    let validatorConfig: CreateClaimCampaignBody['validatorConfig'];
    switch (data.validatorType) {
      case 'EXTERNAL_API':
        validatorConfig = {
          type: 'EXTERNAL_API',
          endpoint: data.externalEndpoint,
          method: data.externalMethod,
          successPath: data.externalSuccessPath,
          errorPath: data.externalErrorPath || undefined,
          timeout: Number(data.externalTimeout),
        };
        break;
      case 'PREGEN_CODE':
        validatorConfig = { type: 'PREGEN_CODE' };
        break;
      case 'SHARED_CODE':
        validatorConfig = { type: 'SHARED_CODE' };
        break;
      case 'DIRECT':
        validatorConfig = { type: 'DIRECT' };
        break;
    }

    let matchCheckType: ClaimMatchCheckType | undefined;
    let matchCheckConfig: CreateClaimCampaignBody['matchCheckConfig'] | undefined;
    if (data.campaignType === 'MATCH') {
      matchCheckType = 'BQ_QUERY';
      matchCheckConfig = {
        type: 'BQ_QUERY',
        bqQuery: data.bqQuery,
        checks: getValidChecks(data.checks),
      };
    }

    const actions = data.actions.map((action) => {
      switch (action.type) {
        case 'TIER_UPGRADE':
          return {
            type: 'TIER_UPGRADE' as const,
            targetTier: action.targetTier,
            reasonCode: 'G',
            remarks: action.remarks,
          };
        case 'BONUS_POINTS':
          return {
            type: 'BONUS_POINTS' as const,
            bonusPoints: Number(action.bonusPoints),
            activityDescription: action.activityDescription,
          };
        case 'STATUS_CREDITS':
          return {
            type: 'STATUS_CREDITS' as const,
            statusCredits: Number(action.statusCredits),
            bonusStatusCredits: action.bonusStatusCredits ? Number(action.bonusStatusCredits) : undefined,
            activityDescription: action.activityDescription,
          };
        case 'DYNAMIC_TIER_UPGRADE':
          return {
            type: 'DYNAMIC_TIER_UPGRADE' as const,
            rewardMapping: action.rewardMapping.map((r) => ({
              match: r.match,
              targetTier: r.targetTier,
              remarks: r.remarks,
            })),
            reasonCode: 'G',
          };
      }
    });

    return {
      name: data.name.trim(),
      description: data.description.trim() || undefined,
      emailVerificationDomains: getEmailVerificationDomainsPayload(data.emailVerificationDomains),
      status: data.status,
      type: data.campaignType,
      validatorType: data.validatorType,
      validatorConfig,
      requireApproval: data.requireApproval,
      evidenceFields: data.requireApproval
        ? data.evidenceFields
            .filter((f) => f.name.trim())
            .map((f) => ({
              name: f.name,
              label: f.label,
              type: f.type,
              ...(f.type === 'dropdown' && f.options
                ? {
                    options: f.options
                      .split(',')
                      .map((o) => o.trim())
                      .filter(Boolean),
                  }
                : {}),
              ...(f.type === 'file' ? { multiple: true } : {}),
            }))
        : undefined,
      matchCheckType,
      matchCheckConfig,
      actions,
      maxClaimsPerUser: Number(data.maxClaimsPerUser),
      ...(data.requireApproval ? { maxAppeals: Number(data.maxAppeals) } : {}),
      ...(data.campaignType === 'MATCH' ? { enrollmentValidityDays: Number(data.enrollmentValidityDays) } : {}),
      startDate: data.startDate ? dayjs(data.startDate).toISOString() : undefined,
      endDate: data.endDate ? dayjs(data.endDate).toISOString() : undefined,
    };
  }, []);

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (data.actions.length === 0) {
        enqueueSnackbar('At least one action is required', { variant: 'error' });
        return;
      }
      if (data.campaignType === 'MATCH' && getValidChecks(data.checks).length === 0) {
        await trigger('checks');
        enqueueSnackbar('At least one eligibility check is required for MATCH campaigns', { variant: 'error' });
        return;
      }
      const dynamicTierActionWithEmptyMapping = data.actions.find(
        (a) => a.type === 'DYNAMIC_TIER_UPGRADE' && a.rewardMapping.length === 0,
      );
      if (dynamicTierActionWithEmptyMapping) {
        enqueueSnackbar('Dynamic Tier Upgrade requires at least one reward mapping row', { variant: 'error' });
        return;
      }
      try {
        if (isEditMode && campaign) {
          const payload = buildPayload(data);
          const updateBody: UpdateClaimCampaignBody = {
            name: payload.name,
            description: payload.description,
            status: payload.status,
            maxClaimsPerUser: payload.maxClaimsPerUser,
            ...(payload.maxAppeals !== undefined && { maxAppeals: payload.maxAppeals }),
            ...(payload.enrollmentValidityDays !== undefined && {
              enrollmentValidityDays: payload.enrollmentValidityDays,
            }),
            matchCheckType: payload.matchCheckType,
            matchCheckConfig: payload.matchCheckConfig,
            evidenceFields: payload.evidenceFields,
            endDate: payload.endDate,
            emailVerificationDomains: payload.emailVerificationDomains,
          };
          if (!fieldsLocked) {
            updateBody.validatorConfig = payload.validatorConfig;
            updateBody.actions = payload.actions;
            updateBody.startDate = payload.startDate;
          }
          await updateMutation.mutateAsync({ campaignId: campaign.id, body: updateBody });
        } else {
          await createMutation.mutateAsync(buildPayload(data));
        }
        onClose();
      } catch {
        // Error handled in mutation hooks
      }
    },
    [
      isEditMode,
      campaign,
      createMutation,
      updateMutation,
      buildPayload,
      onClose,
      fieldsLocked,
      trigger,
      enqueueSnackbar,
    ],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Claim Campaign' : 'Create Claim Campaign'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {fieldsLocked && (
            <Alert severity="info">
              Validator, actions, and start date cannot be changed after claims have been made. You can still update
              verified email domains.
            </Alert>
          )}

          {/* Basic info */}
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field, fieldState: { error } }) => (
              <TextField {...field} label="Name" required fullWidth error={!!error} helperText={error?.message} />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => <TextField {...field} label="Description" fullWidth multiline rows={2} />}
          />

          <Controller
            name="emailVerificationDomains"
            control={control}
            rules={{ validate: validateEmailVerificationDomainsInput }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Verified email domains"
                placeholder="amazon.com, partner.org"
                fullWidth
                error={!!error}
                helperText={getEmailVerificationDomainsHelperText(isEmailVerificationDomainsFocused, error?.message)}
                onFocus={() => setIsEmailVerificationDomainsFocused(true)}
                onBlur={() => {
                  setIsEmailVerificationDomainsFocused(false);
                  field.onBlur();
                }}
              />
            )}
          />

          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr 1fr' }} gap={2}>
            <Controller
              name="campaignType"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label="Campaign Type" fullWidth disabled={isEditMode}>
                  {CLAIM_CAMPAIGN_TYPES.map((ct) => (
                    <MenuItem key={ct} value={ct}>
                      {ct === 'NORMAL' ? 'Normal' : 'Match'}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label="Status" fullWidth>
                  {CLAIM_CAMPAIGN_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="maxClaimsPerUser"
              control={control}
              rules={{
                required: 'Required',
                validate: (v) => Number(v) >= 1 || 'Must be at least 1',
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Max Claims Per User"
                  type="number"
                  fullWidth
                  inputProps={{ min: 1 }}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Box>

          {/* Dates */}
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Start Date"
                  type="datetime-local"
                  fullWidth
                  disabled={fieldsLocked}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
            <Controller
              name="endDate"
              control={control}
              rules={{
                required: requireApproval ? 'End date is required when CS Approval is enabled' : false,
                validate: (value) => {
                  const start = getValues('startDate');
                  if (start && value && dayjs(value).isBefore(dayjs(start))) {
                    return 'End date must be after start date';
                  }
                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="End Date"
                  type="datetime-local"
                  fullWidth
                  required={requireApproval}
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Box>

          <Divider />

          {/* Validator type */}
          <Typography variant="subtitle1" fontWeight={600}>
            Validator
          </Typography>
          <Controller
            name="validatorType"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label="Validator Type" fullWidth disabled={fieldsLocked}>
                {CLAIM_VALIDATOR_TYPES.map((vt) => (
                  <MenuItem key={vt} value={vt}>
                    {VALIDATOR_LABELS[vt]}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {/* External API config fields — shown only for EXTERNAL_API validator */}
          {validatorType === 'EXTERNAL_API' && (
            <Stack spacing={2} sx={{ pl: 2, borderLeft: 3, borderColor: 'divider' }}>
              <Controller
                name="externalEndpoint"
                control={control}
                rules={{ required: 'Endpoint is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Endpoint URL"
                    required
                    fullWidth
                    disabled={fieldsLocked}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <Controller
                  name="externalMethod"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} select label="HTTP Method" fullWidth disabled={fieldsLocked}>
                      {EXTERNAL_API_METHODS.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  name="externalTimeout"
                  control={control}
                  rules={{ required: 'Timeout is required', validate: (v) => Number(v) > 0 || 'Must be positive' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Timeout (ms)"
                      type="number"
                      fullWidth
                      disabled={fieldsLocked}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Box>
              <Controller
                name="externalSuccessPath"
                control={control}
                rules={{ required: 'Success path is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Success JSONPath"
                    required
                    fullWidth
                    disabled={fieldsLocked}
                    error={!!error}
                    helperText={error?.message || 'JSONPath expression to check for success in response'}
                  />
                )}
              />
              <Controller
                name="externalErrorPath"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Error JSONPath (optional)"
                    fullWidth
                    disabled={fieldsLocked}
                    helperText="JSONPath expression for error details in response"
                  />
                )}
              />
            </Stack>
          )}

          {/* Require Approval toggle + evidence fields */}
          <Controller
            name="requireApproval"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} disabled={isEditMode} />}
                label="Require CS Approval"
              />
            )}
          />

          {requireApproval && (
            <Stack spacing={2} sx={{ pl: 2, borderLeft: 3, borderColor: 'divider' }}>
              <Controller
                name="maxAppeals"
                control={control}
                rules={{
                  validate: (v) => {
                    // Skip validation when the field is hidden (requireApproval toggled off)
                    if (!requireApproval) return true;
                    if (v === '' || v == null) return 'Required';
                    const n = Number(v);
                    return (
                      (Number.isInteger(n) && n >= MIN_APPEALS && n <= MAX_APPEALS) ||
                      `Must be a whole number between ${MIN_APPEALS} and ${MAX_APPEALS}`
                    );
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Max Appeals"
                    type="number"
                    fullWidth
                    inputProps={{ min: MIN_APPEALS, max: MAX_APPEALS, step: 1 }}
                    error={!!error}
                    helperText={error?.message || 'Number of times a customer may re-submit after a rejection'}
                  />
                )}
              />
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">Evidence Fields</Typography>
                {!fieldsLocked && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => appendEvidenceField({ name: '', label: '', type: 'text', options: '' })}
                  >
                    Add Field
                  </Button>
                )}
              </Box>
              {evidenceFieldEntries.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No evidence fields — customers will submit free-form evidence.
                </Typography>
              )}
              {evidenceFieldEntries.map((ef, index) => (
                <EvidenceFieldRow
                  key={ef.id}
                  control={control}
                  index={index}
                  disabled={fieldsLocked}
                  onRemove={() => removeEvidenceField(index)}
                />
              ))}
            </Stack>
          )}

          {/* Match configuration — shown only for MATCH campaigns */}
          {campaignType === 'MATCH' && (
            <>
              <Divider />
              <Typography variant="subtitle1" fontWeight={600}>
                Match Configuration
              </Typography>
              <Stack spacing={2} sx={{ pl: 2, borderLeft: 3, borderColor: 'divider' }}>
                <Controller
                  name="enrollmentValidityDays"
                  control={control}
                  rules={{
                    required: 'Required',
                    validate: (v) => {
                      const n = Number(v);
                      return (n >= 1 && n <= 365) || 'Must be between 1 and 365';
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Enrollment Validity (days)"
                      type="number"
                      inputProps={{ min: 1, max: 365 }}
                      sx={{ maxWidth: 250 }}
                      error={!!error}
                      helperText={error?.message || 'Days from enrollment date the customer has to become eligible'}
                    />
                  )}
                />
                <Controller
                  name="bqQuery"
                  control={control}
                  rules={{ required: 'BigQuery query is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="BigQuery Query"
                      multiline
                      rows={4}
                      required
                      fullWidth
                      disabled={fieldsLocked}
                      error={!!error}
                      helperText={
                        error?.message ||
                        'SQL query to fetch eligible customers. Must SELECT customer_bk or customer_id. Use IN UNNEST(@customerIds) to filter enrolled customers.'
                      }
                      inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
                    />
                  )}
                />
                {/* Eligibility checks */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">Eligibility Checks</Typography>
                  {!fieldsLocked && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => appendCheck({ key: '', eligibilityTemplate: '' })}
                    >
                      Add Check
                    </Button>
                  )}
                </Box>

                {checkEntries.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    At least one eligibility check is required.
                  </Typography>
                )}
                {checkEntries.map((entry, index) => (
                  <Stack key={entry.id} spacing={1} sx={{ pl: 2, borderLeft: 2, borderColor: 'primary.light' }}>
                    <Box display="flex" gap={1} alignItems="center">
                      <Controller
                        name={`checks.${index}.key`}
                        control={control}
                        rules={{ required: 'Key is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            label="Key"
                            size="small"
                            sx={{ flex: 1 }}
                            disabled={fieldsLocked}
                            placeholder="e.g. min_nights"
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                      />
                      {!fieldsLocked && (
                        <IconButton size="small" onClick={() => removeCheck(index)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Controller
                      name={`checks.${index}.eligibilityTemplate`}
                      control={control}
                      rules={{ required: 'Template is required' }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label="Eligibility Template"
                          multiline
                          rows={1}
                          fullWidth
                          disabled={fieldsLocked}
                          error={!!error}
                          helperText={error?.message || 'E.g. <%= total_nights >= 3 %>'}
                          inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
                        />
                      )}
                    />
                  </Stack>
                ))}

                {/* Test match check */}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={testMatchMutation.isPending ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                  disabled={testMatchMutation.isPending || !bqQuery || !watchedChecks?.some((c) => c.key.trim())}
                  onClick={() =>
                    testMatchMutation.mutate({
                      bqQuery,
                      checks: getValidChecks(getValues('checks') ?? []),
                      customerId: user?.id_member ?? '',
                    })
                  }
                >
                  {testMatchMutation.isPending ? 'Running...' : 'Test Match Checks'}
                </Button>

                {testMatchMutation.error && (
                  <Alert severity="error">
                    {testMatchMutation.error instanceof Error
                      ? testMatchMutation.error.message
                      : String(testMatchMutation.error)}
                  </Alert>
                )}

                {testMatchMutation.data &&
                  (() => {
                    const rows = testMatchMutation.data.result.rows.map((row, i) => ({ id: i, ...row }));
                    const columns: GridColDef[] =
                      rows.length > 0
                        ? Object.keys(testMatchMutation.data.result.rows[0]).map((key) => ({
                            field: key,
                            headerName: key,
                            flex: 1,
                            minWidth: 120,
                            renderCell: ({ value }) => {
                              if (key === 'eligible') {
                                return (
                                  <Chip
                                    label={value ? 'Yes' : 'No'}
                                    size="small"
                                    color={value ? 'success' : 'default'}
                                  />
                                );
                              }
                              return String(value ?? '');
                            },
                          }))
                        : [];

                    return rows.length > 0 ? (
                      <Box sx={{ height: 300 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {testMatchMutation.data.result.rowCount} row
                          {testMatchMutation.data.result.rowCount !== 1 ? 's' : ''} returned
                        </Typography>
                        <DataGridPro rows={rows} columns={columns} density="compact" disableRowSelectionOnClick />
                      </Box>
                    ) : (
                      <Alert severity="info">No rows returned. Check your query syntax.</Alert>
                    );
                  })()}
              </Stack>
            </>
          )}

          <Divider />

          {/* Actions */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>
              Actions
            </Typography>
            {!fieldsLocked && (
              <Button size="small" startIcon={<AddIcon />} onClick={() => append({ ...DEFAULT_ACTION })}>
                Add Action
              </Button>
            )}
          </Box>

          {fields.map((field, index) => (
            <ActionFormSection
              key={field.id}
              control={control}
              index={index}
              disabled={fieldsLocked}
              onRemove={() => remove(index)}
              dropdownFields={dropdownFields}
            />
          ))}

          {fields.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              At least one action is required.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit, (errors) => {
            enqueueSnackbar(formatValidationErrors(errors), { variant: 'error' });
          })}
          disabled={isPending}
        >
          {isEditMode ? (isPending ? 'Saving...' : 'Save') : isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Evidence field row — extracted to scope useWatch per-index
// ---------------------------------------------------------------------------

interface EvidenceFieldRowProps {
  control: ReturnType<typeof useForm<FormData>>['control'];
  index: number;
  disabled?: boolean;
  onRemove: () => void;
}

function EvidenceFieldRow({ control, index, disabled, onRemove }: EvidenceFieldRowProps) {
  const fieldType = useWatch({ control, name: `evidenceFields.${index}.type` });

  return (
    <Stack spacing={1} sx={{ pl: 1, borderLeft: 2, borderColor: 'grey.300', pb: 1 }}>
      <Box display="grid" gridTemplateColumns="2fr 2fr 100px auto" gap={1} alignItems="start">
        <Controller
          name={`evidenceFields.${index}.name`}
          control={control}
          rules={{ required: 'Required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Field name"
              size="small"
              disabled={disabled}
              error={!!error}
              helperText={error?.message}
              placeholder="e.g. program"
            />
          )}
        />
        <Controller
          name={`evidenceFields.${index}.label`}
          control={control}
          rules={{ required: 'Required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Display label"
              size="small"
              disabled={disabled}
              error={!!error}
              helperText={error?.message}
              placeholder="e.g. Loyalty Program"
            />
          )}
        />
        <Controller
          name={`evidenceFields.${index}.type`}
          control={control}
          render={({ field }) => (
            <TextField {...field} label="Type" size="small" select disabled={disabled}>
              {EVIDENCE_FIELD_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {titleCase(t)}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        {!disabled && (
          <IconButton size="small" onClick={onRemove} sx={{ mt: 0.5 }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {fieldType === 'dropdown' && (
        <Controller
          name={`evidenceFields.${index}.options`}
          control={control}
          rules={{
            validate: (v) =>
              (v ?? '')
                .split(',')
                .map((o) => o.trim())
                .filter(Boolean).length > 0 || 'At least one option required',
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Dropdown options (comma-separated)"
              size="small"
              fullWidth
              disabled={disabled}
              error={!!error}
              helperText={error?.message || 'e.g. Booking.com, Virgin AU, Hilton Honors'}
            />
          )}
        />
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Action form sub-section
// ---------------------------------------------------------------------------

interface ActionFormSectionProps {
  control: ReturnType<typeof useForm<FormData>>['control'];
  index: number;
  disabled?: boolean;
  onRemove: () => void;
  dropdownFields: Array<{ name: string; label: string; options: string[] }>;
}

/**
 * Renders dynamic form fields for a single action based on its type.
 */
function ActionFormSection({ control, index, disabled, onRemove, dropdownFields }: ActionFormSectionProps) {
  return (
    <Stack spacing={2} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">Action {index + 1}</Typography>
        {!disabled && (
          <Tooltip title="Remove action">
            <IconButton size="small" onClick={onRemove}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Controller
        name={`actions.${index}.type`}
        control={control}
        render={({ field }) => (
          <TextField {...field} select label="Action Type" fullWidth disabled={disabled}>
            {CLAIM_ACTION_TYPES.map((at) => (
              <MenuItem key={at} value={at}>
                {ACTION_TYPE_LABELS[at]}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <ActionDynamicFields control={control} index={index} disabled={disabled} dropdownFields={dropdownFields} />
    </Stack>
  );
}

interface ActionDynamicFieldsProps {
  control: ReturnType<typeof useForm<FormData>>['control'];
  index: number;
  disabled?: boolean;
  dropdownFields: Array<{ name: string; label: string; options: string[] }>;
}

/**
 * Renders the appropriate form fields based on the action type.
 * Uses Controller-based watching to re-render when the type changes.
 */
function ActionDynamicFields({ control, index, disabled, dropdownFields }: ActionDynamicFieldsProps) {
  return (
    <Controller
      name={`actions.${index}.type`}
      control={control}
      render={({ field: { value: currentType } }) => {
        switch (currentType) {
          case 'TIER_UPGRADE':
            return (
              <Stack spacing={2}>
                <Controller
                  name={`actions.${index}.targetTier`}
                  control={control}
                  rules={{ required: 'Target tier is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      label="Target Tier"
                      fullWidth
                      disabled={disabled}
                      error={!!error}
                      helperText={error?.message}
                    >
                      {CLAIM_TARGET_TIERS.map((tier) => (
                        <MenuItem key={tier} value={tier}>
                          {titleCase(tier)}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  name={`actions.${index}.remarks`}
                  control={control}
                  rules={{ required: 'Remarks are required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Remarks"
                      required
                      fullWidth
                      disabled={disabled}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Stack>
            );
          case 'BONUS_POINTS':
            return (
              <Stack spacing={2}>
                <Controller
                  name={`actions.${index}.bonusPoints`}
                  control={control}
                  rules={{
                    required: 'Bonus points is required',
                    validate: (v) => Number(v) > 0 || 'Must be greater than 0',
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Bonus Points"
                      type="number"
                      required
                      fullWidth
                      disabled={disabled}
                      inputProps={{ min: 1 }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
                <Controller
                  name={`actions.${index}.activityDescription`}
                  control={control}
                  rules={{ required: 'Activity description is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Activity Description"
                      required
                      fullWidth
                      disabled={disabled}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Stack>
            );
          case 'STATUS_CREDITS':
            return (
              <Stack spacing={2}>
                <Controller
                  name={`actions.${index}.statusCredits`}
                  control={control}
                  rules={{
                    required: 'Status credits is required',
                    validate: (v) => Number(v) > 0 || 'Must be greater than 0',
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Status Credits"
                      type="number"
                      required
                      fullWidth
                      disabled={disabled}
                      inputProps={{ min: 1 }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
                <Controller
                  name={`actions.${index}.bonusStatusCredits`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Bonus Status Credits (optional)"
                      type="number"
                      fullWidth
                      disabled={disabled}
                      inputProps={{ min: 0 }}
                    />
                  )}
                />
                <Controller
                  name={`actions.${index}.activityDescription`}
                  control={control}
                  rules={{ required: 'Activity description is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Activity Description"
                      required
                      fullWidth
                      disabled={disabled}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Stack>
            );
          case 'DYNAMIC_TIER_UPGRADE':
            return (
              <RewardMappingEditor
                control={control}
                actionIndex={index}
                disabled={disabled}
                dropdownFields={dropdownFields}
              />
            );
          default:
            return null;
        }
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Reward mapping editor for DYNAMIC_TIER_UPGRADE
// ---------------------------------------------------------------------------

interface RewardMappingEditorProps {
  control: ReturnType<typeof useForm<FormData>>['control'];
  actionIndex: number;
  disabled?: boolean;
  dropdownFields: Array<{ name: string; label: string; options: string[] }>;
}

function RewardMappingEditor({ control, actionIndex, disabled, dropdownFields }: RewardMappingEditorProps) {
  const {
    fields: rows,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `actions.${actionIndex}.rewardMapping` as 'actions.0.rewardMapping',
  });

  const matchColumnWidth = `minmax(120px, 1fr)`;
  const gridTemplate = [
    ...dropdownFields.map(() => matchColumnWidth),
    '130px',
    'minmax(150px, 1fr)',
    ...(!disabled ? ['40px'] : []),
  ].join(' ');

  return (
    <Stack spacing={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">Reward Mapping</Typography>
        {!disabled && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={dropdownFields.length === 0}
            onClick={() =>
              append({
                id: String(Date.now()),
                match: Object.fromEntries(dropdownFields.map((df) => [df.name, ''])),
                targetTier: 'silver',
                remarks: '',
              })
            }
          >
            Add Row
          </Button>
        )}
      </Box>

      {dropdownFields.length === 0 && (
        <Alert severity="warning">Add dropdown evidence fields first to define match criteria.</Alert>
      )}

      {rows.length > 0 && (
        <Stack spacing={1}>
          {/* Header row */}
          <Box display="grid" gridTemplateColumns={gridTemplate} gap={1} px={1}>
            {dropdownFields.map((df) => (
              <Typography key={df.name} variant="caption" color="text.secondary">
                {df.label}
              </Typography>
            ))}
            <Typography variant="caption" color="text.secondary">
              Target Tier
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Remarks
            </Typography>
            {!disabled && <Box />}
          </Box>

          {/* Data rows */}
          {rows.map((row, rowIdx) => (
            <Box key={row.id} display="grid" gridTemplateColumns={gridTemplate} gap={1} alignItems="center">
              {dropdownFields.map((df) => (
                <Controller
                  key={df.name}
                  name={`actions.${actionIndex}.rewardMapping.${rowIdx}.match.${df.name}`}
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField {...field} select size="small" disabled={disabled} error={!!error}>
                      {df.options.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              ))}
              <Controller
                name={`actions.${actionIndex}.rewardMapping.${rowIdx}.targetTier`}
                control={control}
                rules={{ required: 'Required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} select size="small" disabled={disabled} error={!!error}>
                    {CLAIM_TARGET_TIERS.map((tier) => (
                      <MenuItem key={tier} value={tier}>
                        {titleCase(tier)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <Controller
                name={`actions.${actionIndex}.rewardMapping.${rowIdx}.remarks`}
                control={control}
                rules={{ required: 'Required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    size="small"
                    disabled={disabled}
                    error={!!error}
                    placeholder="e.g. Booking.com Genius 3 match"
                  />
                )}
              />
              {!disabled && (
                <IconButton size="small" onClick={() => remove(rowIdx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {rows.length === 0 && dropdownFields.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          No reward mappings yet. Add rows to map evidence values to tier upgrades.
        </Typography>
      )}
    </Stack>
  );
}
